/* eslint func-style: 'off' */

import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import {
  // actions
  FETCH_FEEDS,
  CREATE_FEED,
  UPDATE_FEED,
  DELETE_FEED,
  FETCH_CURATED_FEEDS,
  FETCH_CAMPAIGN,
  UPDATE_CAMPAIGN,
  // action creators
  fetchFeedsSucceeded,
  fetchFeedsFailed,
  createFeedSucceeded,
  createFeedFailed,
  updateFeedSucceeded,
  updateFeedFailed,
  deleteFeedSucceeded,
  deleteFeedFailed,
  fetchCuratedFeedsSucceeded,
  fetchCuratedFeedsFailed,
  fetchCampaignSucceeded,
  fetchCampaignFailed,
  updateCampaignSucceeded,
  updateCampaignFailed,
  setBanner
} from './NarrativeFeeds.ducks';
import apiClient from 'helpers/apiClient';

const fetchFeeds = function*({ payload }) {
  try {
    const { accountId, campaignId } = payload;
    const path = `/api/accounts/${accountId}/campaigns/${campaignId}/narrative-feeds`;
    const response = yield call(apiClient, path);

    yield put(fetchFeedsSucceeded(response.results || []));
  } catch (e) {
    yield put(fetchFeedsFailed());
    yield put(
      setBanner({
        message: e.body?.error || 'Failed to fetch narrative feeds',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const createFeed = function*({ payload }) {
  try {
    const { accountId, campaignId, name } = payload;
    const path = `/api/accounts/${accountId}/campaigns/${campaignId}/narrative-feeds`;
    const feed = {
      name,
      profile_ids: [],
      includeAll: false
    };

    const response = yield call(apiClient, path, {
      method: 'POST',
      body: JSON.stringify(feed)
    });

    yield put(createFeedSucceeded(response));
    yield put(
      setBanner({
        message: 'A new feed has been created, and results should be available in approximately 48 hours.',
        title: 'Success',
        type: 'success'
      })
    );
  } catch (e) {
    yield put(createFeedFailed());
    yield put(
      setBanner({
        message: e.body?.error || 'Failed to create feed',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const updateFeed = function*({ payload }) {
  try {
    const { accountId, campaignId, feed } = payload;

    if (!feed.profile_ids || !feed.profile_ids.length) {
      yield put(updateFeedFailed());
      yield put(
        setBanner({
          message: 'Please select at least one dataset.',
          title: 'Error',
          type: 'error'
        })
      );
      return;
    }

    const path = `/api/accounts/${accountId}/campaigns/${campaignId}/narrative-feeds/${feed.id}`;

    const response = yield call(apiClient, path, {
      method: 'PUT',
      body: JSON.stringify(feed)
    });
    yield put(updateFeedSucceeded(response));
    yield put(
      setBanner({
        message: 'Feed saved successfully',
        title: 'Success',
        type: 'success'
      })
    );
  } catch (e) {
    yield put(updateFeedFailed());
    yield put(
      setBanner({
        message: e.body?.error || 'Failed to update feed',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const deleteFeed = function*({ payload }) {
  try {
    const { accountId, campaignId, feedId } = payload;

    if (!feedId) {
      // If the feed doesn't have an ID, it hasn't been saved to the server yet
      yield put(deleteFeedSucceeded(feedId));
      return;
    }

    const path = `/api/accounts/${accountId}/campaigns/${campaignId}/narrative-feeds/${feedId}`;

    yield call(apiClient, path, {
      method: 'DELETE'
    });

    yield put(deleteFeedSucceeded(feedId));
    yield put(
      setBanner({
        message: 'Feed deleted successfully',
        title: 'Success',
        type: 'success'
      })
    );
  } catch (e) {
    yield put(deleteFeedFailed());
    yield put(
      setBanner({
        message: e.body?.error || 'Failed to delete feed',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const fetchFeedsSaga = function*() {
  yield takeLatest(FETCH_FEEDS, fetchFeeds);
};

const createFeedSaga = function*() {
  yield takeLatest(CREATE_FEED, createFeed);
};

const updateFeedSaga = function*() {
  yield takeLatest(UPDATE_FEED, updateFeed);
};

const deleteFeedSaga = function*() {
  yield takeLatest(DELETE_FEED, deleteFeed);
};

const fetchCuratedFeeds = function*({ payload: { accountId } }) {
  try {
    const path = `/api/accounts/${accountId}/narrative-feeds`;
    const response = yield call(apiClient, path);
    yield put(fetchCuratedFeedsSucceeded(response || []));
  } catch (e) {
    yield put(fetchCuratedFeedsFailed());
    yield put(
      setBanner({
        message: e.body || 'Failed to fetch curated feeds',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const fetchCuratedFeedsSaga = function*() {
  yield takeLatest(FETCH_CURATED_FEEDS, fetchCuratedFeeds);
};

const fetchCampaign = function*({ payload: { accountId, campaignId } }) {
  try {
    const path = `/api/accounts/${accountId}/campaigns/${campaignId}`;
    const payload = yield call(apiClient, path);
    yield put(fetchCampaignSucceeded(payload));
  } catch (e) {
    yield put(fetchCampaignFailed(e.body));
    yield put(
      setBanner({
        message: e.body?.error || 'Failed to fetch workspace',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const updateCampaign = function*({ payload: campaign }) {
  try {
    const path = `/api/accounts/${campaign.accountId}/campaigns/${campaign.id}`;
    const payload = yield call(apiClient, path, {
      method: 'PUT',
      body: JSON.stringify({narrative_feeds: campaign.narrative_feeds})
    });
    yield put(updateCampaignSucceeded(payload));
    yield put(
      setBanner({
        message: 'Workspace updated successfully',
        title: 'Success',
        type: 'success'
      })
    );
  } catch (e) {
    yield put(updateCampaignFailed(e.body));
    yield put(
      setBanner({
        message: e.body?.error || 'Failed to update workspace',
        title: 'Error',
        type: 'error'
      })
    );
  }
};

const fetchCampaignSaga = function*() {
  yield takeLatest(FETCH_CAMPAIGN, fetchCampaign);
};

const updateCampaignSaga = function*() {
  yield takeLatest(UPDATE_CAMPAIGN, updateCampaign);
};

const sagas = function*() {
  yield all([
    fork(fetchFeedsSaga),
    fork(createFeedSaga),
    fork(updateFeedSaga),
    fork(deleteFeedSaga),
    fork(fetchCuratedFeedsSaga),
    fork(fetchCampaignSaga),
    fork(updateCampaignSaga)
  ]);
};

export default sagas;
export {
  fetchFeeds,
  createFeed,
  updateFeed,
  deleteFeed,
  fetchCuratedFeeds,
  fetchCampaign,
  updateCampaign,
  fetchFeedsSaga,
  createFeedSaga,
  updateFeedSaga,
  deleteFeedSaga,
  fetchCuratedFeedsSaga,
  fetchCampaignSaga,
  updateCampaignSaga
};
